import { A11yModule, FocusMonitor, FocusTrap, FocusTrapFactory, LiveAnnouncer } from '@angular/cdk/a11y';
import { OverlayModule } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  inject,
  input,
  model,
  OnInit,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxButton } from '../button/button';
import { type HSVA, hsvaToHex, hsvaToHslaString, hsvaToRgbaString, parseColorToHsva } from './color-utils';
import { RecentColorsService } from './recent-colors.service';

@Component({
  selector: 'hx-color-picker',
  templateUrl: './color-picker.html',
  styleUrls: ['./color-picker.css', './color-picker-tokens.css', '../../styles/form-control-tokens.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [OverlayModule, A11yModule, HxButton],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HxColorPicker),
      multi: true,
    },
  ],
})
export class HxColorPicker extends HxBaseControl implements OnInit, ControlValueAccessor {
  private readonly focusMonitor = inject(FocusMonitor);
  private readonly focusTrapFactory = inject(FocusTrapFactory);
  private readonly recentColorsService = inject(RecentColorsService);
  private readonly liveAnnouncer = inject(LiveAnnouncer);
  private focusTrap?: FocusTrap;
  private previouslyFocusedElement?: HTMLElement;
  private lastAnnounceTime = 0;
  private readonly announceThrottle = 500; // ms

  // ControlValueAccessor callbacks
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  // Tooltip state
  protected readonly hoveredColor = signal<string | null>(null);
  private hoverTimeout?: number;

  // Expose Math for template
  protected readonly Math = Math;

  // Inputs
  readonly value = model<string>('#FF0000'); // Default to pure red
  readonly brandColors = input<string[]>([]);
  readonly showPalettes = input<boolean>(true); // Show design system color palettes
  readonly format = input<'hex' | 'rgba' | 'hsla'>('hex');
  override readonly ariaLabel = input<string | undefined>('Color picker');

  // Computed deduplicated brand colors
  protected readonly uniqueBrandColors = computed(() => {
    const colors = this.brandColors();
    return [...new Set(colors.map((c) => c.toUpperCase()))];
  });

  // Design System Color Palettes (dynamically read from CSS custom properties)
  protected readonly colorPalettes = computed(() => {
    const palettes = [
      { name: 'Primary', prefix: 'primary-primary' },
      { name: 'Secondary', prefix: 'secondary-secondary' },
      { name: 'Neutral', prefix: 'neutral-neutral' },
      { name: 'Info', prefix: 'info-info' },
      { name: 'Error', prefix: 'error-error' },
      { name: 'Warning', prefix: 'warning-warning' },
      { name: 'Success', prefix: 'success-success' },
      { name: 'Base', prefix: 'base', shades: ['white', 'black'] },
    ];

    const styles = getComputedStyle(document.documentElement);

    return palettes
      .map((palette) => {
        const colors = palette.shades
          ? palette.shades.map((shade, idx) => ({
              id: `${palette.prefix}-${shade}`,
              value: styles.getPropertyValue(`--${palette.prefix}-${shade}`).trim(),
            }))
          : [50, 100, 200, 300, 400, 500, 600, 700, 800, 900].map((shade, idx) => ({
              id: `${palette.prefix}-${shade}`,
              value: styles.getPropertyValue(`--${palette.prefix}-${shade}`).trim(),
            }));

        // Filter out empty values (CSS variables not found)
        const validColors = colors.filter((color) => color.value && color.value !== '');

        return { name: palette.name, colors: validColors };
      })
      .filter((palette) => palette.colors.length > 0); // Only include palettes with valid colors
  });

  // Check if design system tokens are available
  protected readonly hasDesignSystemColors = computed(() => {
    return this.colorPalettes().length > 0;
  });

  // Helper to detect light colors that need border
  protected isLightColor(color: string): boolean {
    // Parse color to RGB
    const hex = color.startsWith('#') ? color : '#' + color;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Consider colors with luminance > 0.9 as light (needing border)
    return luminance > 0.9;
  }

  // Outputs
  readonly valueChange = output<string>();

  // Internal state (HSVA)
  protected readonly h = signal<number>(0); // Hue 0-360
  protected readonly s = signal<number>(1); // Saturation 0-1
  protected readonly v = signal<number>(1); // Value 0-1
  protected readonly a = signal<number>(1); // Alpha 0-1

  // Overlay state
  protected readonly isOpen = signal<boolean>(false);

  // Active tab ('quick' or 'custom')
  protected readonly activeTab = signal<'quick' | 'custom'>('quick');

  // Current format (internal state that syncs with input)
  protected readonly currentFormat = signal<'hex' | 'rgba' | 'hsla'>('hex');

  // Input validation
  protected readonly inputError = signal<string>('');
  private pendingInputValue = '';

  // Recent colors
  protected readonly recentColors = signal<string[]>([]);

  // View children
  protected readonly trigger = viewChild.required<ElementRef<HTMLButtonElement>>('trigger');
  private readonly overlayPanel = viewChild<ElementRef<HTMLDivElement>>('overlayPanel');
  protected readonly gridContainer = viewChild<ElementRef<HTMLDivElement>>('gridContainer');

  constructor() {
    super();
    // Load recent colors on init
    this.recentColors.set(this.recentColorsService.getRecentColors());

    // Sync currentFormat with format input
    effect(() => {
      this.currentFormat.set(this.format());
    });

    // Parse initial value to HSVA
    effect(() => {
      const val = this.value();
      if (val) {
        const hsva = parseColorToHsva(val);
        if (hsva) {
          this.h.set(hsva.h);
          this.s.set(hsva.s);
          this.v.set(hsva.v);
          this.a.set(hsva.a);
        }
      }
    });

    // Emit changes when HSVA updates and add to recent
    effect(() => {
      const hsva: HSVA = { h: this.h(), s: this.s(), v: this.v(), a: this.a() };
      const format = this.currentFormat();
      let color: string;

      switch (format) {
        case 'rgba':
          color = hsvaToRgbaString(hsva);
          break;
        case 'hsla':
          color = hsvaToHslaString(hsva);
          break;
        default:
          color = hsvaToHex(hsva);
      }

      this.value.set(color);
      this.valueChange.emit(color);
      this.onChange(color); // Notify Angular forms
      // Always store as HEX to avoid duplicates when only opacity changes
      const hexColor = hsvaToHex(hsva);
      this.recentColorsService.addRecentColor(hexColor);
      this.recentColors.set(this.recentColorsService.getRecentColors());
    });
  }

  protected togglePicker(): void {
    if (this.disabledState()) {
      return;
    }
    const willOpen = !this.isOpen();
    if (willOpen) {
      this.previouslyFocusedElement = document.activeElement as HTMLElement;
    }
    this.isOpen.set(willOpen);
  }

  protected setActiveTab(tab: 'quick' | 'custom'): void {
    this.activeTab.set(tab);
  }

  protected closePicker(): void {
    this.isOpen.set(false);
    this.focusTrap?.destroy();
    this.focusTrap = undefined;
    this.onTouched(); // Mark control as touched
    // Restore focus to trigger
    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus();
      this.previouslyFocusedElement = undefined;
    }
  }

  protected onBackdropClick(): void {
    this.closePicker();
  }

  protected onEscapeKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closePicker();
    }
  }

  protected onOverlayAttach(): void {
    // Create focus trap after overlay is attached to DOM
    const panel = this.overlayPanel()?.nativeElement;
    if (!this.focusTrap && panel) {
      this.focusTrap = this.focusTrapFactory.create(panel);
      this.focusTrap.focusInitialElement();
    }
  }

  // Grid interaction handlers
  private isGridDragging = false;

  protected onGridMouseDown(event: MouseEvent): void {
    event.preventDefault();
    this.updateGridPosition(event.clientX, event.clientY);
    this.isGridDragging = true;

    const onMouseMove = (e: MouseEvent) => {
      if (this.isGridDragging) {
        this.updateGridPosition(e.clientX, e.clientY);
      }
    };

    const onMouseUp = () => {
      this.isGridDragging = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  protected onGridTouchStart(event: TouchEvent): void {
    event.preventDefault();
    const touch = event.touches[0];
    if (touch) {
      this.updateGridPosition(touch.clientX, touch.clientY);
      this.isGridDragging = true;
    }

    const onTouchMove = (e: TouchEvent) => {
      if (this.isGridDragging) {
        const touch = e.touches[0];
        if (touch) {
          this.updateGridPosition(touch.clientX, touch.clientY);
        }
      }
    };

    const onTouchEnd = () => {
      this.isGridDragging = false;
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };

    document.addEventListener('touchmove', onTouchMove);
    document.addEventListener('touchend', onTouchEnd);
  }

  private updateGridPosition(clientX: number, clientY: number): void {
    const grid = this.gridContainer()?.nativeElement;
    if (!grid) {
      return;
    }

    const rect = grid.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const y = Math.max(0, Math.min(clientY - rect.top, rect.height));

    const saturation = x / rect.width;
    const value = 1 - y / rect.height;

    this.s.set(saturation);
    this.v.set(value);
  }

  protected onGridKeyDown(event: KeyboardEvent): void {
    const step = event.shiftKey ? 0.1 : 0.01;
    const pageStep = 0.25;
    let changed = false;

    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        this.s.set(Math.min(1, this.s() + step));
        changed = true;
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.s.set(Math.max(0, this.s() - step));
        changed = true;
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.v.set(Math.min(1, this.v() + step));
        changed = true;
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.v.set(Math.max(0, this.v() - step));
        changed = true;
        break;
      case 'PageUp':
        event.preventDefault();
        this.v.set(Math.min(1, this.v() + pageStep));
        changed = true;
        break;
      case 'PageDown':
        event.preventDefault();
        this.v.set(Math.max(0, this.v() - pageStep));
        changed = true;
        break;
      case 'Home':
        event.preventDefault();
        this.v.set(1);
        changed = true;
        break;
      case 'End':
        event.preventDefault();
        this.v.set(0);
        changed = true;
        break;
    }

    if (changed) {
      this.announceGridChange();
    }
  }

  // Slider event handlers
  protected onHueChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const hue = parseFloat(input.value);
    this.h.set(hue);
  }

  protected onAlphaChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const alpha = parseFloat(input.value) / 100;
    this.a.set(alpha);
  }

  // Helper to get opaque color for alpha slider gradient
  protected getOpaqueColor(): string {
    const hsva: HSVA = { h: this.h(), s: this.s(), v: this.v(), a: 1 };
    return hsvaToHex(hsva);
  }

  // Format toggle handler
  protected setFormat(newFormat: 'hex' | 'rgba' | 'hsla'): void {
    // Update the current format
    this.currentFormat.set(newFormat);

    // Convert current HSVA to new format
    const hsva: HSVA = { h: this.h(), s: this.s(), v: this.v(), a: this.a() };
    let color: string;

    switch (newFormat) {
      case 'rgba':
        color = hsvaToRgbaString(hsva);
        break;
      case 'hsla':
        color = hsvaToHslaString(hsva);
        break;
      default:
        color = hsvaToHex(hsva);
    }

    this.value.set(color);
    this.inputError.set('');
  }

  // Manual input handlers
  protected onManualInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.pendingInputValue = input.value.trim();
    this.inputError.set('');
  }

  protected validateAndApplyInput(): void {
    if (!this.pendingInputValue) {
      return;
    }

    const hsva = parseColorToHsva(this.pendingInputValue);
    if (hsva) {
      this.h.set(hsva.h);
      this.s.set(hsva.s);
      this.v.set(hsva.v);
      this.a.set(hsva.a);
      this.inputError.set('');
    } else {
      this.inputError.set('Invalid color format');
    }
  }

  // Preset color handler
  protected applyPresetColor(color: string): void {
    const hsva = parseColorToHsva(color);
    if (hsva) {
      this.h.set(hsva.h);
      this.s.set(hsva.s);
      this.v.set(hsva.v);
      this.a.set(hsva.a);
      this.announceColorChange(color);
    }
  }

  // Quick color handler (doesn't close overlay)
  protected applyQuickColor(color: string): void {
    const hsva = parseColorToHsva(color);
    if (hsva) {
      this.h.set(hsva.h);
      this.s.set(hsva.s);
      this.v.set(hsva.v);
      this.a.set(hsva.a);
      this.announceColorChange(color);
      // Don't close overlay - user can keep selecting colors
    }
  }

  // Live announcer helper (throttled)
  private announceColorChange(color: string): void {
    const now = Date.now();
    if (now - this.lastAnnounceTime >= this.announceThrottle) {
      this.liveAnnouncer.announce(`Color selected: ${color}`, 'polite');
      this.lastAnnounceTime = now;
    }
  }

  // Keyboard navigation for grid changes
  private announceGridChange(): void {
    const now = Date.now();
    if (now - this.lastAnnounceTime >= this.announceThrottle) {
      const hsva: HSVA = { h: this.h(), s: this.s(), v: this.v(), a: this.a() };
      const color = hsvaToHex(hsva);
      this.liveAnnouncer.announce(`Color: ${color}`, 'polite');
      this.lastAnnounceTime = now;
    }
  }

  // EyeDropper support
  protected readonly eyeDropperSupported = signal<boolean>(false);
  private eyeDropper?: any;

  ngOnInit(): void {
    // Feature detect EyeDropper API
    if ('EyeDropper' in window) {
      this.eyeDropperSupported.set(true);
    }
  }

  protected async openEyeDropper(): Promise<void> {
    if (!this.eyeDropperSupported()) {
      return;
    }

    try {
      // @ts-ignore - EyeDropper is not in TypeScript definitions yet
      this.eyeDropper = new window.EyeDropper();
      const result = await this.eyeDropper.open();

      if (result?.sRGBHex) {
        const hsva = parseColorToHsva(result.sRGBHex);
        if (hsva) {
          this.h.set(hsva.h);
          this.s.set(hsva.s);
          this.v.set(hsva.v);
          this.a.set(hsva.a);
          this.announceColorChange(result.sRGBHex);
        }
      }
    } catch (error) {
      // User cancelled or error occurred
      console.debug('EyeDropper cancelled or failed:', error);
    }
  }

  // ControlValueAccessor Implementation
  writeValue(value: string): void {
    if (value) {
      this.value.set(value);
      const hsva = parseColorToHsva(value);
      if (hsva) {
        this.h.set(hsva.h);
        this.s.set(hsva.s);
        this.v.set(hsva.v);
        this.a.set(hsva.a);
      }
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabledFromCVA.set(isDisabled);
  }
}
